import { CodeBlock, InfoBox, SectionTitle, SubsectionTitle } from './shared';

export function RemoteAccessContent() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-4">远程访问（SSH、隧道和 tailnet）</h1>
        <p className="text-lg text-slate-400 leading-relaxed">
          通过在专用主机上运行单个 Gateway 网关（主节点）并让客户端连接到它来支持"SSH 远程"。
        </p>
      </div>

      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
        <p className="text-slate-300 leading-relaxed mb-3">
          <strong className="text-white">对于操作员：</strong>SSH 隧道是通用的回退方案。
        </p>
        <p className="text-slate-300 leading-relaxed">
          <strong className="text-white">对于节点（iOS/Android）：</strong>连接到 Gateway WebSocket。
        </p>
      </div>

      <SectionTitle id="core-concept">核心理念</SectionTitle>
      <div className="space-y-3 text-slate-300 leading-relaxed">
        <p>Gateway WebSocket 绑定到 loopback（默认 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">18789</code>）。</p>
        <p>远程使用通过 SSH 转发该 loopback 端口（或使用 tailnet/VPN）。</p>
      </div>

      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
        <p className="text-slate-400 text-sm font-mono">命令流：Telegram → Gateway 网关 → 智能体 → 节点</p>
      </div>

      <SectionTitle id="common-scenarios">3 种常见 VPN / tailnet 设置场景</SectionTitle>

      <SubsectionTitle id="scenario-1">场景 1：tailnet 中始终在线的 Gateway 网关（VPS 或家庭服务器）</SubsectionTitle>
      <p className="text-slate-300 leading-relaxed">
        在持久主机上运行，通过 Tailscale 或 SSH 访问。Gateway 网关在 VPS 或家庭服务器上持续运行，操作员通过 Tailscale 或 SSH 隧道远程连接。
      </p>

      <SubsectionTitle id="scenario-2">场景 2：家庭桌面运行 Gateway 网关，笔记本电脑作为远程控制</SubsectionTitle>
      <p className="text-slate-300 leading-relaxed">
        使用 macOS 应用的 <strong className="text-white">Remote over SSH</strong> 模式。桌面机器运行 Gateway 网关，笔记本电脑通过 SSH 远程控制。
      </p>

      <SubsectionTitle id="scenario-3">场景 3：笔记本电脑运行 Gateway 网关，从其他机器远程访问</SubsectionTitle>
      <p className="text-slate-300 leading-relaxed">
        SSH 隧道或 Tailscale Serve。笔记本电脑运行 Gateway 网关，其他设备通过 SSH 隧道或 Tailscale Serve 访问。
      </p>

      <SectionTitle id="ssh-tunnel">SSH 隧道命令</SectionTitle>
      <CodeBlock
        language="bash"
        code={`ssh -N -L 18789:127.0.0.1:18789 user@host`}
      />

      <SectionTitle id="cli-remote-defaults">CLI 远程默认值</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        在配置文件中设置远程连接参数：
      </p>
      <CodeBlock
        language="json5"
        code={`{
  gateway: {
    mode: "remote",
    remote: {
      url: "ws://127.0.0.1:18789",
      token: "your-token",
    },
  },
}`}
      />

      <SectionTitle id="webchat-ssh">通过 SSH 的聊天 UI</SectionTitle>
      <p className="text-slate-300 leading-relaxed">
        WebChat 直接连接到 Gateway WebSocket，通过 SSH 转发 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">18789</code> 端口即可使用。
      </p>

      <SectionTitle id="macos-remote-ssh">macOS 应用"Remote over SSH"</SectionTitle>
      <p className="text-slate-300 leading-relaxed">
        端到端驱动相同设置，操作手册：
        <a href="/platforms/mac/remote.html" className="text-green-400 hover:text-green-300 ml-1 transition-colors">/platforms/mac/remote.html</a>
      </p>

      <SectionTitle id="security-rules">安全规则</SectionTitle>
      <div className="space-y-3">
        <div className="flex items-start gap-3 bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <i className="ri-shield-check-line text-green-400 text-lg mt-0.5 flex-shrink-0"></i>
          <p className="text-slate-300 text-sm">保持 Gateway 网关仅 loopback</p>
        </div>
        <div className="flex items-start gap-3 bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <i className="ri-shield-check-line text-green-400 text-lg mt-0.5 flex-shrink-0"></i>
          <p className="text-slate-300 text-sm">Loopback + SSH/Tailscale Serve 是最安全的默认设置</p>
        </div>
        <div className="flex items-start gap-3 bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <i className="ri-shield-check-line text-green-400 text-lg mt-0.5 flex-shrink-0"></i>
          <p className="text-slate-300 text-sm">非 loopback 绑定必须使用身份验证令牌/密码</p>
        </div>
        <div className="flex items-start gap-3 bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <i className="ri-information-line text-cyan-400 text-lg mt-0.5 flex-shrink-0"></i>
          <p className="text-slate-300 text-sm">
            <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">gateway.remote.token</code> 仅用于远程 CLI 调用
          </p>
        </div>
        <div className="flex items-start gap-3 bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <i className="ri-information-line text-cyan-400 text-lg mt-0.5 flex-shrink-0"></i>
          <p className="text-slate-300 text-sm">
            <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">gateway.remote.tlsFingerprint</code> 在使用 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">wss://</code> 时固定远程 TLS 证书
          </p>
        </div>
      </div>

      <InfoBox type="info" title="提示">
        Loopback + SSH/Tailscale Serve 是最安全的默认设置。非 loopback 绑定必须配置身份验证令牌。
      </InfoBox>
    </div>
  );
}

export default RemoteAccessContent;
